import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProjectsListVM } from '../lib/vm/useProjectsListVM';
import {
  Banner,
  Button,
  EmptyState,
  MonoLabel,
  RecordCard,
  RecordCardList,
  Row,
  Spinner,
  Tag
} from '../components/primitives';
import {
  Table,
  type Column,
  type Row as TableRow,
  type SortDescriptor,
  type TableUrlStateAdapter,
  type UrlState
} from '../components/Table';
import {
  ListControlBar,
  ViewTabs,
  type FacetDef,
  type FilterDict,
  type GroupByOption,
  type PropertyChoice
} from '../components/list-controls';
import {
  ProjectsGallery,
  GALLERY_CHIP_KEYS,
  GALLERY_CHIP_LABELS,
  GALLERY_DEFAULT_CHIPS,
  type GalleryChipKey
} from '../components/projects/ProjectsGallery';
import { CreateProjectModal } from '../components/projects/CreateProjectModal';
import { formatRelativeTime } from '../lib/relativeTime';
import {
  getProjectsView,
  setProjectsView,
  resolveVisibleProperties
} from '../lib/viewPreferences';
import type {
  ProjectsTab,
  ProjectListState,
  ProjectsGroupBy
} from '../lib/models/projects';
import type { ProjectListItem } from '../lib/projects/useProjectsList';
import './Projects.css';

/**
 * `/projects` — list page styled to match `/documents`.
 *
 * Composes the shared list-controls primitives over `useProjectsListVM`:
 * view tabs (Gallery | Table), a control bar (Search · + Filter · active
 * chips · Group by · Properties), and either `<ProjectsGallery>` or the
 * `<Table>` primitive depending on `state.tab`. Layout width + chrome mirror
 * the Documents page exactly; the page keeps its Workspace eyebrow + subtitle.
 */

// ── View tabs / Group by ────────────────────────────────────────────────
const TAB_OPTIONS = [
  { key: 'gallery', label: 'Gallery' },
  { key: 'table', label: 'Table' }
] as const;

const GROUP_BY_OPTIONS: ReadonlyArray<GroupByOption> = [
  { key: 'none', label: 'None' },
  { key: 'status', label: 'Status' },
  { key: 'lead', label: 'Lead' }
];

// ── Table columns ───────────────────────────────────────────────────────
const TABLE_DEFAULT_SORT: SortDescriptor = {
  columnKey: 'created_at',
  direction: 'desc'
};

const TABLE_COLUMN_KEYS = [
  'name',
  'status',
  'lead',
  'progress',
  'dod',
  'created_at',
  'target_date'
] as const;

type TableColumnKey = (typeof TABLE_COLUMN_KEYS)[number];

const TABLE_COLUMN_LABELS: Readonly<Record<TableColumnKey, string>> = {
  name: 'Name',
  status: 'Status',
  lead: 'Lead',
  progress: 'Progress',
  dod: 'DoD',
  created_at: 'Created',
  target_date: 'Target'
};

const TABLE_DEFAULT_VISIBLE: ReadonlyArray<TableColumnKey> = [
  'name',
  'status',
  'lead',
  'progress',
  'dod'
];

const TABLE_LOCKED_COLUMNS: ReadonlyArray<string> = ['name'];

const COLS_STORAGE_KEY_TABLE = 'penrose:projects:cols:table';
const COLS_STORAGE_KEY_GALLERY = 'penrose:projects:cols:gallery';

/**
 * Bridge `state.sort` ↔ Table primitive's URL adapter. Same same-value gating
 * as the Documents page (see `Documents.tsx`) so Table's "sync to URL" effect
 * can't loop the VM's `replaceState` past the browser quota.
 */
function buildTableUrlAdapter(
  currentSort: SortDescriptor,
  onSortChange: (next: SortDescriptor) => void,
  visibleCols: ReadonlyArray<string>,
  onColsChange: (next: ReadonlyArray<string>) => void
): TableUrlStateAdapter {
  return {
    read(): UrlState {
      return { sort: currentSort, cols: [...visibleCols] };
    },
    write(s: UrlState) {
      if (
        s.sort &&
        (s.sort.columnKey !== currentSort.columnKey ||
          s.sort.direction !== currentSort.direction)
      ) {
        onSortChange(s.sort);
      }
      if (s.cols && s.cols.length > 0) {
        const sameLen = s.cols.length === visibleCols.length;
        const sameOrder = sameLen && s.cols.every((k, i) => k === visibleCols[i]);
        if (!sameOrder) onColsChange(s.cols);
      }
    }
  };
}

/** Resolve a Projects table column key → its display value for a card chip. */
function projectCardChipValue(key: string, item: ProjectListItem): string | null {
  const { project, leadName, doneCount, totalCount, dodMet, dodTotal } = item;
  switch (key) {
    case 'status':
      return project.status || null;
    case 'lead':
      return leadName ?? 'No lead';
    case 'progress':
      return `${doneCount}/${totalCount} done`;
    case 'dod':
      return `${dodMet}/${dodTotal} DoD`;
    case 'created_at':
      return project.created_at ? formatRelativeTime(project.created_at) : null;
    case 'target_date':
      return project.target_date || null;
    default:
      return null;
  }
}

/**
 * Narrow-viewport card for the Projects Table tab — mirrors the Documents
 * table→card fallback. Renders the project name plus a chip per visible
 * (non-name) column. CSS hides the `<Table>` and shows this list below 880px.
 */
function ProjectPropertyCard({
  item,
  chipKeys,
  onOpen
}: {
  item: ProjectListItem;
  chipKeys: ReadonlyArray<string>;
  onOpen: () => void;
}) {
  const chips = chipKeys
    .map((k) => ({ key: k, value: projectCardChipValue(k, item) }))
    .filter((c) => c.value != null);

  return (
    <RecordCard
      layout="stack"
      interactive
      onActivate={onOpen}
      data-testid={`projects-table-card-${item.project.id}`}
      data-card-id={item.project.id}
    >
      <RecordCard.Title>{item.project.name}</RecordCard.Title>
      {chips.length > 0 ? (
        <RecordCard.Meta>
          {chips.map((chip) => (
            <RecordCard.Chip
              key={chip.key}
              className={`pn-record-card-chip--${chip.key}`}
              data-chip-key={chip.key}
            >
              {`${
                TABLE_COLUMN_LABELS[chip.key as TableColumnKey] ?? chip.key
              }: ${chip.value}`}
            </RecordCard.Chip>
          ))}
        </RecordCard.Meta>
      ) : null}
    </RecordCard>
  );
}

export function Projects() {
  // HashRouter seam: the VM hydrates from `window.location.search`, which is
  // empty under HashRouter, so read deep-link params here and seed the VM.
  const [searchParams] = useSearchParams();
  const initialOverrides = useMemo(() => {
    const rawTab = searchParams.get('tab');
    const tab: ProjectsTab =
      rawTab === 'gallery' || rawTab === 'table'
        ? rawTab
        : (getProjectsView() ?? 'gallery');
    const status = searchParams
      .getAll('status')
      .flatMap((v) => v.split(',').map((s) => s.trim()))
      .filter(Boolean);
    const overrides: Partial<ProjectListState> = { tab };
    if (status.length > 0) overrides.status = status;
    return overrides;
    // Computed once for the initial VM seed; the VM owns URL state thereafter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vm = useProjectsListVM(initialOverrides);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const rows = vm.derived.rows;
  const loading = vm.derived.loading;
  const errored = vm.status === 'error';
  const noOrg = vm.status === 'empty';
  const hasFilters = vm.derived.hasActiveFilters;
  const showFilteredEmpty =
    !loading && !errored && rows.length === 0 && hasFilters;
  const showZeroEmpty =
    !loading && !errored && vm.status === 'ready' && vm.derived.total === 0 && !hasFilters;

  const countLabel = `${vm.derived.total} ${vm.derived.total === 1 ? 'project' : 'projects'}`;

  // ── Pagination ───────────────────────────────────────────────────────
  const pageSize = vm.derived.pageSize;
  const total = vm.derived.total;
  const page = vm.state.page;
  const pageCount = total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const rangeStart = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = total > 0 ? Math.min(page * pageSize, total) : 0;
  const showFooter = !loading && !errored && !noOrg && total > pageSize;
  const prevDisabled = page <= 1;
  const nextDisabled = page * pageSize >= total;

  // ── Facets ─────────────────────────────────────────────────────────────
  const facets = useMemo<ReadonlyArray<FacetDef>>(
    () => [
      {
        key: 'status',
        label: 'Status',
        options: vm.derived.statusOptions.map((s) => ({ id: s.id, label: s.label }))
      },
      {
        key: 'lead',
        label: 'Lead',
        options: vm.derived.leadOptions.map((l) => ({ id: l.id, label: l.label }))
      }
    ],
    [vm.derived.statusOptions, vm.derived.leadOptions]
  );

  const filters: FilterDict = useMemo(
    () => ({ status: [...vm.state.status], lead: [...vm.state.lead] }),
    [vm.state.status, vm.state.lead]
  );

  const onFiltersChange = useCallback(
    (next: FilterDict) => {
      vm.actions.setFilter('status', next.status ?? []);
      vm.actions.setFilter('lead', next.lead ?? []);
    },
    [vm.actions]
  );

  // ── Visible properties — per-tab default + URL/localStorage fallback ──
  const visibleProperties = useMemo<ReadonlyArray<string>>(() => {
    const isGallery = vm.state.tab === 'gallery';
    const storedCols =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(
            isGallery ? COLS_STORAGE_KEY_GALLERY : COLS_STORAGE_KEY_TABLE
          )
        : null;
    // Validate against THIS surface's key universe so foreign keys leaked via
    // the shared `?cols=` URL param can't strand the gallery/table (see
    // resolveVisibleProperties).
    return resolveVisibleProperties(
      vm.state.cols,
      storedCols,
      isGallery ? GALLERY_CHIP_KEYS : TABLE_COLUMN_KEYS,
      isGallery ? GALLERY_DEFAULT_CHIPS : TABLE_DEFAULT_VISIBLE
    );
  }, [vm.state.cols, vm.state.tab]);

  const setVisibleProperties = useCallback(
    (next: ReadonlyArray<string>) => {
      vm.actions.setCols(next);
      if (typeof window !== 'undefined') {
        const key =
          vm.state.tab === 'gallery'
            ? COLS_STORAGE_KEY_GALLERY
            : COLS_STORAGE_KEY_TABLE;
        window.localStorage.setItem(key, next.join(','));
      }
    },
    [vm.actions, vm.state.tab]
  );

  const propertyChoices: ReadonlyArray<PropertyChoice> = useMemo(() => {
    if (vm.state.tab === 'gallery') {
      return GALLERY_CHIP_KEYS.map((k: GalleryChipKey) => ({
        key: k,
        label: GALLERY_CHIP_LABELS[k]
      }));
    }
    return TABLE_COLUMN_KEYS.map((k) => ({ key: k, label: TABLE_COLUMN_LABELS[k] }));
  }, [vm.state.tab]);

  const propertyLockedKeys = vm.state.tab === 'table' ? TABLE_LOCKED_COLUMNS : [];

  // ── Table columns + rows (read-only) ────────────────────────────────────
  const tableColumns = useMemo<Column[]>(() => {
    const all: Record<TableColumnKey, Column> = {
      name: { key: 'name', label: 'Name', type: 'text', editable: false },
      status: { key: 'status', label: 'Status', type: 'text', editable: false },
      lead: { key: 'lead', label: 'Lead', type: 'text', editable: false },
      progress: { key: 'progress', label: 'Progress', type: 'text', editable: false },
      dod: { key: 'dod', label: 'DoD', type: 'text', editable: false },
      created_at: { key: 'created_at', label: 'Created', type: 'text', editable: false },
      target_date: { key: 'target_date', label: 'Target', type: 'text', editable: false }
    };
    return TABLE_COLUMN_KEYS.map((k) => all[k]);
  }, []);

  const tableRows = useMemo<TableRow[]>(
    () =>
      rows.map((it) => ({
        id: it.project.id,
        name: it.project.name,
        status: it.project.status,
        lead: it.leadName ?? 'No lead',
        progress: `${it.doneCount}/${it.totalCount}`,
        dod: `${it.dodMet}/${it.dodTotal}`,
        created_at: it.project.created_at
          ? formatRelativeTime(it.project.created_at)
          : '',
        target_date: it.project.target_date ?? ''
      })),
    [rows]
  );

  const cardChipKeys = useMemo(
    () =>
      TABLE_COLUMN_KEYS.filter(
        (k) => k !== 'name' && visibleProperties.includes(k)
      ),
    [visibleProperties]
  );

  // The Projects table is read-only — every column is `editable: false` and
  // there are no bulk actions, so these required Table callbacks are no-ops.
  const noopCellEdit = useCallback(async () => {}, []);
  const noopBulkAction = useCallback(async () => {}, []);

  const tableUrlAdapter = useMemo(
    () =>
      buildTableUrlAdapter(
        vm.state.sort,
        vm.actions.setSort,
        visibleProperties,
        setVisibleProperties
      ),
    [vm.state.sort, vm.actions.setSort, visibleProperties, setVisibleProperties]
  );

  return (
    <section
      className="pn-projects"
      data-testid="projects-route"
      data-status={vm.status}
      data-active-org-id={vm.activeOrgId ?? ''}
      data-row-count={rows.length}
      data-total={vm.derived.total}
      data-loading={loading ? '1' : '0'}
      data-page={vm.state.page}
      data-tab={vm.state.tab}
      data-group-by={vm.state.groupBy}
      data-status-filter={vm.state.status.join(',')}
      data-lead-filter={vm.state.lead.join(',')}
      data-q={vm.state.q}
    >
      <header className="pn-projects-head">
        <div className="pn-projects-head-row">
          <div className="pn-projects-head-title">
            <MonoLabel size="xs" tracking="wide">Workspace</MonoLabel>
            <div className="pn-projects-title-row">
              <h1 className="pn-projects-title">Projects</h1>
              <Tag data-testid="projects-count">{countLabel}</Tag>
            </div>
            <p className="pn-projects-sub">
              Org-private containers grouping epics, work items, documents and mockups.
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setCreating(true)}
            data-testid="projects-new"
            disabled={!vm.derived.canCreate}
          >
            + New project
          </Button>
        </div>
      </header>

      <ViewTabs
        options={TAB_OPTIONS.map((t) => ({ key: t.key, label: t.label }))}
        current={vm.state.tab}
        onChange={(next) => {
          vm.actions.setTab(next as ProjectsTab);
          setProjectsView(next as ProjectsTab);
        }}
        ariaLabel="Projects views"
        testIdPrefix="projects-route"
      />

      <ListControlBar
        testIdPrefix="projects"
        activeTab={vm.state.tab}
        searchQuery={vm.state.q}
        onSearchChange={vm.actions.setSearch}
        searchPlaceholder="Search projects…"
        searchAriaLabel="Search projects"
        filters={filters}
        onFiltersChange={onFiltersChange}
        facets={facets}
        groupByOptions={GROUP_BY_OPTIONS}
        groupBy={vm.state.groupBy}
        onGroupByChange={(next) =>
          vm.actions.setGroupBy(next as ProjectsGroupBy)
        }
        propertiesMode={vm.state.tab === 'table' ? 'columns' : 'chips'}
        propertyChoices={propertyChoices}
        propertyLockedKeys={propertyLockedKeys}
        visibleProperties={visibleProperties}
        onVisiblePropertiesChange={setVisibleProperties}
      />

      {errored ? (
        <EmptyState data-testid="projects-error">
          Couldn’t load projects{vm.derived.error ? `: ${vm.derived.error}` : ''}.
        </EmptyState>
      ) : noOrg ? (
        <Banner kind="default" title="No active org">
          Join or create an org to start grouping work into projects.
        </Banner>
      ) : loading ? (
        <div className="pn-projects-loading" data-testid="projects-loading">
          <Spinner label="Loading projects" />
        </div>
      ) : showFilteredEmpty ? (
        <EmptyState
          data-testid="projects-empty-filtered"
          action={
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                vm.actions.setFilter('status', []);
                vm.actions.setFilter('lead', []);
                vm.actions.setSearch('');
              }}
              data-testid="projects-clear-filters"
            >
              Clear filters
            </Button>
          }
        >
          No projects match your filters.
        </EmptyState>
      ) : showZeroEmpty ? (
        <EmptyState
          data-testid="projects-empty"
          action={
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCreating(true)}
              data-testid="projects-empty-new"
            >
              Create your first project
            </Button>
          }
        >
          No projects yet. Create one to group related work.
        </EmptyState>
      ) : vm.state.tab === 'gallery' ? (
        <ProjectsGallery
          rows={rows}
          visibleChips={visibleProperties}
          groupBy={vm.state.groupBy}
          onRowOpen={(id) => navigate(`/projects/${id}`)}
        />
      ) : (
        <div className="pn-projects-table-wrap">
          <Table
            columns={tableColumns}
            rows={tableRows}
            defaultSort={TABLE_DEFAULT_SORT}
            onCellEdit={noopCellEdit}
            onBulkAction={noopBulkAction}
            urlState={tableUrlAdapter}
            dataTestId="projects"
            onRowOpen={(id) => navigate(`/projects/${id}`)}
          />
          <RecordCardList
            className="pn-projects-table-cards"
            data-testid="projects-table-cards"
          >
            {rows.map((it) => (
              <ProjectPropertyCard
                key={it.project.id}
                item={it}
                chipKeys={cardChipKeys}
                onOpen={() => navigate(`/projects/${it.project.id}`)}
              />
            ))}
          </RecordCardList>
        </div>
      )}

      {showFooter ? (
        <Row
          template="1fr auto"
          className="pn-projects-footer"
          data-testid="projects-footer"
          data-page={page}
          data-page-count={pageCount}
        >
          <span className="pn-projects-footer-range" data-testid="projects-footer-range">
            Showing {rangeStart}–{rangeEnd} of {total}
          </span>
          <span className="pn-projects-footer-nav">
            <Button
              variant="ghost"
              size="sm"
              disabled={prevDisabled}
              onClick={() => vm.actions.setPage(page - 1)}
              data-testid="projects-footer-prev"
            >
              Prev
            </Button>
            <span className="pn-projects-footer-page" data-testid="projects-footer-page">
              Page {page} of {pageCount}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={nextDisabled}
              onClick={() => vm.actions.setPage(page + 1)}
              data-testid="projects-footer-next"
            >
              Next
            </Button>
          </span>
        </Row>
      ) : null}

      <CreateProjectModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          navigate(`/projects/${id}`);
        }}
      />
    </section>
  );
}
